import { pendingThreadAttachmentsAtom, pendingThreadMessagesAtom } from "@/atoms/messagesAtoms.ts";
import { parseUnitsExhaustedError } from "@/components/billing/UnitsExhaustedDialog.tsx";
import { buildOptimisticUserMessage } from "@/components/messages/hooks/useSendMessage.tsx";
import { OmniboxTasksPanel, useOmniboxTasks } from "@/components/tasks/OmniboxTasks.tsx";
import { useSendInBackgroundEnabled } from "./useSendInBackground.ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { errorToast } from "@/components/ui/toast.tsx";
import { api } from "@/lib/http.ts";
import { useAtomValue, useSetAtom } from "jotai";
import { Paperclip } from "lucide-react";
import React, {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "wouter";
import { useEmbedded } from "../../../hooks/useEmbedded.tsx";
import {
  createNewTopicThread,
  updateThreadOptimistically,
  useStopThreadExecution,
} from "../../../hooks/useThreads.tsx";
import { useCurrentUserContext } from "../../../hooks/useUser.tsx";
import { cn, isCoarsePointer } from "../../../lib/utils.ts";
import type { NewMessageReq, NewMessageResp } from "../../../types/threadEvents.ts";
import type { GetThreadResp } from "../../../types/thread.ts";
import type { ThreadConfig } from "../../../types/threadConfig.ts";
import type { SendMessageFn } from "../hooks/useMessageList.tsx";
import { ActionButton } from "./ActionButton.tsx";
import { applyEnrichments } from "./applyEnrichments.ts";
import { UI_CONSTANTS } from "./constants.ts";
import { runBackgroundUploadAndSend, seedPendingAttachments } from "./attachmentSend.ts";
import { FileAttachment } from "./FileAttachment.tsx";
import { useFileAttachment } from "./hooks.ts";
import { setRefusedNewChatDraft, useComposerDraft } from "./composerDraft.ts";
import { OmniboxToolsMenu } from "./OmniboxToolsMenu.tsx";
import { SmartTextarea } from "./SmartTextarea.tsx";
import type { AttachedFile } from "./types.ts";
import {
  filterVisibleEnrichments,
  useOmniboxEnrichment,
} from "@/contexts/OmniboxEnrichmentContext.tsx";
import {
  beginNewChatSendAtom,
  lastNewChatPromptAtom,
  rememberNewChatPromptAtom,
} from "@/components/keyboard/navSignals.ts";

/**
 * How far the pill grows before the field scrolls inside it. Five lines was too
 * few to read back a message addressed to a long list of people while the
 * People picker is open, so the pill gives up more of the surface behind it
 * than the shared nine-line ceiling would (BIG-9288).
 */
const PILL_MAX_ROWS = 10;

/**
 * The card's "Ask a follow up" row: eight lines on a phone, thirteen once the
 * card is desktop-wide. It sits inside the artifact card rather than floating
 * over the thread, so it can take more room than the pill. The `em` values are
 * the fallback for browsers without `lh`, at the shared caps' ~1.4 line height.
 */
const FOLLOW_UP_MAX_LINES_CLASS_NAME =
  "max-h-[11.2em] [@supports(height:1lh)]:max-h-[8lh] @md:max-h-[18.2em] @md:[@supports(height:1lh)]:max-h-[13lh]";

const NO_FILES: AttachedFile[] = [];

const hasPendingConfig = (config: ThreadConfig): boolean =>
  !!config.modelPreferences?.provider || !!config.modelPreferences?.agentVersion;

export type FocusType = "always" | "desktop-only" | "never";

interface OmniboxProps extends Omit<
  ComponentProps<"textarea">,
  "rows" | "onSubmit" | "value" | "onChange"
> {
  thread?: GetThreadResp;
  initialRows?: number;
  onSubmit?: SendMessageFn;
  onEscapeKeyPressed?: () => boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  focusType?: FocusType;
  isSendingMessage?: boolean;
  defaultValue?: string;
  /**
   * Imperative ref that the parent can call to set the textarea value directly.
   * Bypasses the defaultValue → useEffect chain so repeated calls with the
   * same text always work (no React state deduplication).
   */
  prefillRef?: React.MutableRefObject<((text: string) => void) | null>;
  /**
   * Focuses the field from outside, mirroring {@link prefillRef}. A page that
   * mounts the omnibox where a button used to be owns the shortcut that button
   * carried, and the right answer to it is now to focus what is already on
   * screen rather than raise a surface over it.
   */
  focusRef?: React.MutableRefObject<(() => void) | null>;
  newThreadOnly?: boolean;
  /**
   * Hide the input chrome (FileAttachment row, AttachButton, ModelSwitcher,
   * ActionButton) so the omnibox renders as a bare textarea. Used by the
   * artifact biscuit overlay where the omnibox is reframed as a single
   * "Ask a follow up" row peer to the action rows.
   */
  hideInputControls?: boolean;
  /**
   * Optional element rendered to the left of the textarea (e.g. a square
   * sparkle badge that mirrors the numbered action-row badges in the
   * biscuit overlay).
   */
  textareaLeadingElement?: React.ReactNode;
  /**
   * Optional override for what happens after a new thread is created from
   * this Omnibox. When provided, replaces the default "navigate to
   * /threads/:id" — used by the calendar's CreateOrAskDialog to open the
   * new thread in the right sidebar instead so the user stays on
   * /calendar. The callback is fired on every code path that previously
   * navigated (text+attachments, text-only, content-less fallback). `background`
   * reflects the submit gesture (see the `sendInBackground` preference): when
   * true the caller should keep the user in place (toast + row flash) instead
   * of opening the thread; when false it should open it.
   */
  onThreadCreated?: (threadId: number, background: boolean) => void;
  /**
   * Opt in to rendering `"page"`-scoped enrichments (e.g. the calendar's
   * implicit view-week chip). Off by default so page-bound enrichments don't
   * leak into composers that merely share the page — a thread sidebar or an
   * artifact "Ask a follow up" row. The calendar's own create-or-ask omnibox
   * sets this. Selection-scoped enrichments always render regardless.
   */
  hostsPageEnrichments?: boolean;
}

export const Omnibox: React.FC<OmniboxProps> = ({
  thread,
  className = "",
  initialRows = 1,
  onSubmit,
  onEscapeKeyPressed,
  onFocus,
  onBlur,
  isSendingMessage = false,
  // Every general composer — canvas, New chat, thread detail — takes this one.
  // Only a composer that means something narrower names its own.
  placeholder = "Message Okthx",
  defaultValue = "",
  prefillRef,
  focusRef,
  focusType = "always",
  newThreadOnly = false,
  hideInputControls = false,
  textareaLeadingElement,
  onThreadCreated,
  hostsPageEnrichments = false,
  ...textareaProps
}) => {
  const { enrichments } = useOmniboxEnrichment();
  // Page-scoped enrichments only surface in omniboxes that opt in; everything
  // else (selection-scoped, the default) surfaces everywhere. Used for both
  // rendering and `buildMessage` so a hidden enrichment never silently rides
  // along on a send.
  const visibleEnrichments = useMemo(
    () => filterVisibleEnrichments(enrichments, hostsPageEnrichments),
    [enrichments, hostsPageEnrichments],
  );
  const queryClient = useQueryClient();
  const setPendingThreadMessages = useSetAtom(pendingThreadMessagesAtom);
  // What ↑ brings back into an empty new-chat composer (BIG-8149). Recorded
  // as typed, before enrichment, since that is what the user would extend —
  // and only once the send has succeeded, so a failed send is not recalled.
  const rememberNewChatPrompt = useSetAtom(rememberNewChatPromptAtom);
  const beginNewChatSend = useSetAtom(beginNewChatSendAtom);
  const lastNewChatPrompt = useAtomValue(lastNewChatPromptAtom);
  const { isWebView } = useEmbedded();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isCreatingThreadRef = useRef(false);
  const [_location, setLocation] = useLocation();
  // Whether a plain-Enter submit defaults to "send in background" (stay put)
  // vs. opening the new thread. ⌘/Ctrl+Enter always does the opposite. See
  // `handleKeyDown`.
  const backgroundIsDefault = useSendInBackgroundEnabled();
  // Caller can opt out of navigating to /threads/:id by providing
  // `onThreadCreated` — e.g. the calendar's CreateOrAskDialog opens the
  // thread in the left aside (or sends it to the background) so the user stays
  // on /calendar. `background` carries the submit gesture through to it.
  const handleThreadCreated = useCallback(
    (threadId: number, background: boolean) => {
      if (onThreadCreated) {
        onThreadCreated(threadId, background);
      } else {
        setLocation(`/threads/${threadId}`);
      }
    },
    [onThreadCreated, setLocation],
  );
  // Keyed by thread so the draft outlives this instance: the card that takes
  // over the composer area mounts its own Omnibox for the same thread, and the
  // text typed in one has to show up in the other.
  const {
    text: value,
    setText: setValue,
    attachedFiles,
    setAttachedFiles,
    restored: restoredDraft,
  } = useComposerDraft(thread?.id, defaultValue);
  // Set once a new-chat send from this composer is refused for lack of AI
  // usage, until one goes through. Meanwhile the box's live text is mirrored
  // into the held draft, so whatever unmounts this composer (either upgrade
  // path out of the dialog) carries over what the box held at that moment,
  // and clearing the box drops it.
  const [holdsRefusedDraft, setHoldsRefusedDraft] = useState(false);
  useEffect(() => {
    if (holdsRefusedDraft) setRefusedNewChatDraft(value);
  }, [holdsRefusedDraft, value]);
  // Buffered config for the home-page omnibox: ModelSwitcher writes its
  // selection here pre-thread, and we forward it on the next
  // `createNewTopicThread` call. Ignored when an existing thread is open
  // (in that mode ModelSwitcher writes straight to /threads/:id/config).
  const [pendingConfig, setPendingConfig] = useState<ThreadConfig>({});

  // Get user timezone and location for client context
  const { getUserTimezone, getUserLocation } = useCurrentUserContext();

  const setPendingThreadAttachments = useSetAtom(pendingThreadAttachmentsAtom);
  const {
    fileInputRef,
    supportedTypes,
    isDraggingOver,
    handleAttachClick,
    handleFileSelect,
    handlePaste,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useFileAttachment(setAttachedFiles);

  // The thread's open tasks, shown as a count on the toolbar and a list at the
  // top of the card. Disabled along with the rest of the chrome rather than
  // merely hidden with it, so a composer that shows no tasks fetches none.
  const tasks = useOmniboxTasks({ threadId: thread?.id, enabled: !hideInputControls });

  // Stop execution functionality
  const stopMutation = useStopThreadExecution({
    threadId: thread?.id || 0,
  });

  const handleStopExecution = useCallback(async () => {
    if (!thread?.id) return;
    try {
      await stopMutation.mutateAsync();
    } catch (error) {
      console.error("Failed to stop thread execution:", error);
    }
  }, [thread?.id, stopMutation]);

  const sendInitialMessageMutation = useMutation({
    mutationFn: async ({ threadId, body }: { threadId: number; body: NewMessageReq }) => {
      const response = await api.post<NewMessageResp>(`/api/a/threads/${threadId}/messages`, body);
      return response.data;
    },
  });

  const createNewThreadAndSend = useCallback(
    async (background: boolean = backgroundIsDefault) => {
      // Prevent duplicate thread creation from rapid submissions
      if (isCreatingThreadRef.current) return;
      isCreatingThreadRef.current = true;
      // Ticketed at the start: the two send paths below finish in any order.
      const sendTicket = beginNewChatSend();
      // The refusal itself is explained by the upgrade dialog the axios
      // interceptor opens; this only keeps the text for after it.
      const holdIfRefused = (err: unknown): boolean => {
        const refused = parseUnitsExhaustedError(err) !== undefined;
        if (refused) setHoldsRefusedDraft(true);
        return refused;
      };

      try {
        const created = await createNewTopicThread({
          title: "",
          artifactIds: [],
          config: hasPendingConfig(pendingConfig) ? pendingConfig : undefined,
        }).catch((err: unknown) => {
          // Keep state so the user can retry.
          if (!holdIfRefused(err)) errorToast("Couldn't start the thread");
          console.error("Failed to create a new thread", err);
          return undefined;
        });
        const newThreadId = created?.id;
        if (!newThreadId) return;
        setHoldsRefusedDraft(false);
        setRefusedNewChatDraft(undefined);

        const enriched = applyEnrichments(value, visibleEnrichments);

        // Attachment send: navigate INSTANTLY with an optimistic message + uploading
        // chips, then upload + POST in a detached controller (this OmniBox unmounts
        // on navigation). Reconciliation rides the `tempClientId` socket event,
        // observable because the thread page is already mounted by then.
        if (attachedFiles.length > 0) {
          const tempClientId = Math.random();
          const body: NewMessageReq = {
            text: enriched.text,
            type: "userRequest",
            agentContent: enriched.agentContent,
            calendarContext: enriched.calendarContext,
            tempClientId,
            clientContext: {
              userTimezone: getUserTimezone(),
              userLocation: getUserLocation() || undefined,
            },
          };

          const { item: optimisticMessage } = buildOptimisticUserMessage(newThreadId, body, {
            status: "completed",
            suppressQueuedPill: true,
            id: tempClientId,
          });
          setPendingThreadMessages((prev) => ({ ...prev, [newThreadId]: optimisticMessage }));
          seedPendingAttachments(
            setPendingThreadAttachments,
            newThreadId,
            tempClientId,
            attachedFiles,
          );
          updateThreadOptimistically(queryClient, newThreadId, { activityState: "running" });

          runBackgroundUploadAndSend({
            threadId: newThreadId,
            attachedFiles,
            body,
            setPending: setPendingThreadAttachments,
            queryClient,
            onSent: () => rememberNewChatPrompt({ text: value, ticket: sendTicket }),
          });

          setValue("");
          setAttachedFiles([]);
          textareaRef.current?.focus();
          handleThreadCreated(newThreadId, background);
          return;
        }

        // Text-only send: no upload, so the POST is fast. Keep the await-then-
        // navigate flow, keying the placeholder on the real event id.
        if (value.trim()) {
          try {
            const userLocation = getUserLocation();
            const body: NewMessageReq = {
              text: enriched.text,
              type: "userRequest",
              agentContent: enriched.agentContent,
              calendarContext: enriched.calendarContext,
              clientContext: {
                userTimezone: getUserTimezone(),
                userLocation: userLocation || undefined,
              },
            };

            const { eventId } = await sendInitialMessageMutation.mutateAsync({
              threadId: newThreadId,
              body,
            });

            // Hand an optimistic placeholder off to the thread we're about to open
            // so its useSessionChat seeds it on mount (no empty-feed flash), and
            // mark the thread running so the OmniBox shows the stop control right
            // away. A brand-new thread is idle, so the message renders straight
            // into the feed (status `completed`, `suppressQueuedPill`). We key the
            // placeholder on the real event id so it dedups against the thread's
            // message-list fetch. If the server didn't return one (slash-command
            // path), skip the handoff — the thread fetch covers it.
            if (eventId !== undefined) {
              const { item: optimisticMessage } = buildOptimisticUserMessage(newThreadId, body, {
                status: "completed",
                suppressQueuedPill: true,
                id: eventId,
              });
              setPendingThreadMessages((prev) => ({ ...prev, [newThreadId]: optimisticMessage }));
            }
            updateThreadOptimistically(queryClient, newThreadId, { activityState: "running" });

            // Clear local input state on successful send, then navigate
            rememberNewChatPrompt({ text: value, ticket: sendTicket });
            setValue("");
            setAttachedFiles([]);
            textareaRef.current?.focus();
            handleThreadCreated(newThreadId, background);
          } catch (err) {
            // Do not navigate on failure; keep state so user can retry
            holdIfRefused(err);
            console.error("Failed to send initial message to new thread", err);
          }
          return;
        }

        // Fallback: no content to send, just navigate to empty thread
        handleThreadCreated(newThreadId, background);
      } finally {
        isCreatingThreadRef.current = false;
      }
    },
    [
      value,
      attachedFiles,
      sendInitialMessageMutation,
      handleThreadCreated,
      rememberNewChatPrompt,
      beginNewChatSend,
      getUserTimezone,
      getUserLocation,
      visibleEnrichments,
      pendingConfig,
      queryClient,
      setPendingThreadMessages,
      setPendingThreadAttachments,
      backgroundIsDefault,
      setValue,
      setAttachedFiles,
    ],
  );

  // Whether there is anything to send. Text or an attachment will do: a file on
  // its own is a message. Read by the send button as well as by the submit
  // handler, so what the button offers and what submitting does cannot disagree.
  //
  // The card's follow-up row (`hideInputControls`) has no chips to show files
  // on, so it never sends them: files attached in the full composer would
  // otherwise ride along on a follow-up with no way to see or remove them. They
  // stay in the thread's draft for the full composer to send.
  const sendableFiles = hideInputControls ? NO_FILES : attachedFiles;
  const canSend = Boolean(value.trim()) || sendableFiles.length > 0;

  const handleSubmit = useCallback(
    async (e: React.FormEvent, background: boolean = backgroundIsDefault) => {
      e.preventDefault();
      if (canSend) {
        if (newThreadOnly) {
          // TODO: needs to be able to handle file uploads and @ mentions
          await createNewThreadAndSend(background);
        } else {
          // Apply enrichment message builders (e.g. time-range context). Mirrors
          // the new-thread path above so ranges selected from the sidebar calendar
          // inside a thread are included in the sent message.
          const enriched = applyEnrichments(value, visibleEnrichments);

          // Hand the raw attachments to the send mutation, which shows the
          // optimistic message + uploading chips immediately and uploads in the
          // background — no foreground upload await blocking the feed. We rely on
          // the parent to report send errors.
          const sent = await onSubmit?.(enriched.text, {
            attachedFiles: sendableFiles.length > 0 ? sendableFiles : undefined,
            agentContent: enriched.agentContent,
            calendarContext: enriched.calendarContext,
          });
          if (sent !== false) {
            // Only clear on success; on error parent returns false and we keep state intact.
            // The draft belongs to the thread, not this instance, so a send that
            // resolves late can find a newer draft in its place; clear only what
            // was sent.
            setValue((current) => (current === value ? "" : current));
            // Files dropped in while the send was pending are not part of it,
            // so the sent ones are removed by id rather than the list cleared.
            if (sendableFiles.length > 0) {
              const sentIds = new Set(sendableFiles.map((f) => f.id));
              setAttachedFiles((current) => current.filter((f) => !sentIds.has(f.id)));
            }
            textareaRef.current?.focus();
          }
        }
      }
    },
    [
      canSend,
      value,
      sendableFiles,
      newThreadOnly,
      createNewThreadAndSend,
      onSubmit,
      visibleEnrichments,
      backgroundIsDefault,
      setValue,
      setAttachedFiles,
    ],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>, hasPopupOpen: boolean) => {
      // Don't handle Enter/Tab if a popup (mention or slash command) is open
      if (hasPopupOpen) {
        return;
      }

      if (e.key === "Escape") {
        // Let enrichments handle Escape first (e.g. clear time range selections)
        for (const enrichment of visibleEnrichments) {
          if (enrichment.onEscape?.()) {
            e.preventDefault();
            return;
          }
        }
        if (onEscapeKeyPressed) {
          e.preventDefault();
          onEscapeKeyPressed();
          return;
        }
        // Nothing claimed the key, so Escape means "let me out of the field".
        // Bare shortcuts stand down while a text field has focus, and a
        // composer with no host handler (the calendar canvas) otherwise left
        // the user with no key that gets them back. Neither prevented nor
        // stopped: a screen-level Escape still does its own job behind this.
        e.currentTarget.blur();
        return;
      }

      // ↑ on an empty new-chat field brings back the last prompt sent from
      // one, caret at its end, to extend and send as a new message. Only when
      // empty: in a draft the arrow is a caret key, and a modifier means the
      // user asked the browser for something else.
      if (
        e.key === "ArrowUp" &&
        newThreadOnly &&
        value === "" &&
        lastNewChatPrompt !== "" &&
        !e.shiftKey &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey
      ) {
        e.preventDefault();
        setValue(lastNewChatPrompt);
        const end = lastNewChatPrompt.length;
        requestAnimationFrame(() => textareaRef.current?.setSelectionRange(end, end));
        return;
      }

      // Enter and ⌘/Ctrl+Enter both submit; they only differ in where the new
      // thread lands. The `sendInBackground` preference sets the plain-Enter
      // default (stay put vs. open the thread); ⌘/Ctrl+Enter does the opposite.

      // ⌘/Ctrl+Enter always submits (the `composer.send` shortcut) — works on
      // mobile and even mid-line, unlike bare Enter below.
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSubmit(e as unknown as React.FormEvent, !backgroundIsDefault);
        return;
      }

      // Enter submits, Shift+Enter is the newline — on a hardware keyboard.
      // A touch keyboard keeps the newline: its return key is the only one a
      // thumb has, and ⌘/Ctrl+Enter above is not reachable there, so binding it
      // to send would leave no way to write a second line. Phones send with the
      // button.
      //
      // Except where there is no button. `hideInputControls` is the artifact
      // biscuit's follow-up row, which renders the field alone — so on a touch
      // keyboard Enter is the only way out, and taking it leaves that row
      // unsubmittable.
      if (e.key === "Enter" && !e.shiftKey && (!isCoarsePointer() || hideInputControls)) {
        e.preventDefault();
        handleSubmit(e as unknown as React.FormEvent, backgroundIsDefault);
      }
    },
    [
      handleSubmit,
      onEscapeKeyPressed,
      visibleEnrichments,
      hideInputControls,
      backgroundIsDefault,
      newThreadOnly,
      value,
      lastNewChatPrompt,
      setValue,
    ],
  );

  // Expose an imperative prefill function via the parent-supplied ref.
  // Calling this directly sets the textarea value and focuses it, bypassing
  // the defaultValue → useEffect chain (immune to React state deduplication).
  useEffect(() => {
    if (!prefillRef) return;
    prefillRef.current = (text: string) => {
      setValue(text);
      textareaRef.current?.setSelectionRange(text.length, text.length);
      if (text.length) {
        textareaRef.current?.focus();
      }
    };
    return () => {
      prefillRef.current = null;
    };
  }, [prefillRef, setValue]);

  // The focus-only counterpart. `prefillRef` deliberately declines to focus for
  // empty text, since seeding an empty value must not steal the cursor on
  // mount; this is the caller saying focus and meaning it.
  useEffect(() => {
    if (!focusRef) return;
    focusRef.current = () => textareaRef.current?.focus();
    return () => {
      focusRef.current = null;
    };
  }, [focusRef]);

  useEffect(() => {
    if (
      !isSendingMessage &&
      textareaRef.current &&
      (focusType === "always" || (focusType === "desktop-only" && !isCoarsePointer()))
    ) {
      textareaRef.current?.focus({
        preventScroll: true, // without this, the focus will break the scrolling in `useActiveChatScroll`
      });
    }
  }, [isSendingMessage, focusType]);

  // A seeded composer opens mid-sentence ("Build me a routine that "), and a
  // composer that picks up the thread's draft opens mid-thought, so the caret
  // belongs after that text rather than in front of it — where a programmatic
  // focus leaves it. The imperative `prefillRef` path already does this; the
  // mount-time one runs before the textarea exists, so it can't.
  //
  // Held in a ref because the caret has to track what the textarea actually
  // contains, and `value` seeds from `defaultValue` once (above) — a later
  // `defaultValue` never reaches the box, so it must not move the caret into a
  // position that text would have had. Reading the ref rather than the prop
  // states that once-only contract to the dependency linter instead of
  // suppressing it.
  const seedRef = useRef(value);
  // Declared after the focus effect so the focus lands first.
  useEffect(() => {
    const seed = seedRef.current;
    if (!seed) return;
    textareaRef.current?.setSelectionRange(seed.length, seed.length);
  }, []);

  const handleFocus = useCallback(() => {
    onFocus?.();
  }, [onFocus]);

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      if (e.currentTarget.contains(e.relatedTarget as Node)) {
        return;
      }
      onBlur?.();
    },
    [onBlur],
  );

  const submitInNewThread = useCallback(
    async (messageText: string, errorLabel: string) => {
      if (isCreatingThreadRef.current) return;
      isCreatingThreadRef.current = true;

      try {
        const created = await createNewTopicThread({
          title: "",
          artifactIds: [],
          config: hasPendingConfig(pendingConfig) ? pendingConfig : undefined,
        }).catch((err: unknown) => {
          if (!parseUnitsExhaustedError(err)) errorToast(errorLabel);
          console.error(errorLabel, err);
          return undefined;
        });
        const newThreadId = created?.id;
        if (!newThreadId) return;

        try {
          const userLocation = getUserLocation();
          const body: NewMessageReq = {
            text: messageText,
            type: "userRequest",
            clientContext: {
              userTimezone: getUserTimezone(),
              userLocation: userLocation || undefined,
            },
          };
          const { eventId } = await sendInitialMessageMutation.mutateAsync({
            threadId: newThreadId,
            body,
          });

          // Same optimistic handoff as createNewThreadAndSend so Quick Schedule /
          // Quick Routine sends also render their message instantly on the new
          // thread instead of flashing an empty feed. See that function for why
          // we key on the real event id.
          if (eventId !== undefined) {
            const { item: optimisticMessage } = buildOptimisticUserMessage(newThreadId, body, {
              status: "completed",
              suppressQueuedPill: true,
              id: eventId,
            });
            setPendingThreadMessages((prev) => ({ ...prev, [newThreadId]: optimisticMessage }));
          }
          updateThreadOptimistically(queryClient, newThreadId, { activityState: "running" });

          // Quick Schedule / Quick Routine are button submits with no key
          // modifier, so they follow the plain-Enter default.
          handleThreadCreated(newThreadId, backgroundIsDefault);
        } catch (err) {
          console.error(errorLabel, err);
        }
      } finally {
        isCreatingThreadRef.current = false;
      }
    },
    [
      getUserLocation,
      getUserTimezone,
      sendInitialMessageMutation,
      handleThreadCreated,
      pendingConfig,
      queryClient,
      setPendingThreadMessages,
      backgroundIsDefault,
    ],
  );

  const handleQuickScheduleSubmit = useCallback(
    (messageText: string) => submitInNewThread(messageText, "Failed to send meeting request"),
    [submitInNewThread],
  );

  const _handleQuickRoutineSubmit = useCallback(
    (messageText: string) =>
      submitInNewThread(messageText, "Failed to send routine creation request"),
    [submitInNewThread],
  );

  if (isWebView) {
    return <div className="hidden" />;
  }

  const composingField = (
    <>
      {textareaLeadingElement}
      <SmartTextarea
        ref={textareaRef}
        value={value}
        onChange={setValue}
        onKeyDown={handleKeyDown}
        onPaste={hideInputControls ? undefined : handlePaste}
        placeholder={placeholder}
        initialRows={initialRows}
        // Ten lines, then it scrolls.
        maxRows={hideInputControls ? undefined : PILL_MAX_ROWS}
        maxLinesClassName={hideInputControls ? FOLLOW_UP_MAX_LINES_CLASS_NAME : undefined}
        initialValueIsTyped={restoredDraft}
        {...textareaProps}
      />
    </>
  );

  // The controls-hidden shape. A leading element needs a row to sit in beside
  // the field; without one the field is the whole thing. Chosen here so the
  // markup below picks between two shapes rather than nesting a second ternary
  // inside the first.
  const bareField = textareaLeadingElement ? (
    <div className="flex w-full items-center gap-2">{composingField}</div>
  ) : (
    composingField
  );

  return (
    <form
      className={cn(
        "relative flex flex-col h-fit max-w-full mx-4 md:mx-0",
        // Two shapes. With its controls hidden the composer is still a plain
        // bordered card, because that is what the thread's follow-up row is and
        // its host styles the form directly. With them shown it is a pill, and
        // the box moves inside to the row so anything stacked above it — task
        // panel, attachments, context chips — sits outside the pill rather than
        // stretching it.
        hideInputControls ? "gap-3 border bg-popover rounded-2xl p-3 @md:p-4" : "gap-2",
        className,
      )}
      onSubmit={handleSubmit}
      onFocus={handleFocus}
      onBlur={handleBlur}
      // Dropping files attaches them. Skipped when the input chrome is hidden
      // (the biscuit "Ask a follow up" row has nowhere to show attachments).
      onDragOver={hideInputControls ? undefined : handleDragOver}
      onDragLeave={hideInputControls ? undefined : handleDragLeave}
      onDrop={hideInputControls ? undefined : handleDrop}
      id="nux-omnibox"
    >
      {!hideInputControls && isDraggingOver && (
        <div className="absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-[1.75rem] border-2 border-dashed border-primary bg-popover/90 text-sm font-medium text-primary pointer-events-none">
          <Paperclip className="size-4" />
          Drop to attach
        </div>
      )}

      {!hideInputControls && <OmniboxTasksPanel tasks={tasks} />}

      {!hideInputControls && (
        <FileAttachment attachedFiles={attachedFiles} onFilesChange={setAttachedFiles} />
      )}

      {visibleEnrichments.map((enrichment) => (
        <React.Fragment key={enrichment.id}>{enrichment.renderContent()}</React.Fragment>
      ))}

      {hideInputControls ? (
        bareField
      ) : (
        // 56px at rest with a 28px radius — half the height, so the ends are
        // true semicircles at rest and the same value becomes an ordinary
        // rounded corner once the field wraps past 56px.
        //
        // `items-end` keeps the buttons on the last line while the text grows
        // upward, up to the field's own `MAX_LINES_*` ceiling.
        //
        // One 11px inset on every side, which centres a 32px button in the pill
        // once the 1px border is counted — (56 − 2 − 32) / 2 — and produces the
        // resting height on its own, so the min-height agrees with it rather
        // than leaving slack for `items-end` to push the buttons around in.
        //
        // The shadow belongs here and not on the form, which has no radius of
        // its own and drew a square corner behind the pill's round end.
        <div className="flex min-h-14 shrink-0 items-end gap-2 rounded-[1.75rem] border bg-popover p-[11px] shadow-2xl">
          <OmniboxToolsMenu
            onAttach={handleAttachClick}
            tasks={tasks}
            thread={thread}
            onConfigChange={setPendingConfig}
            onQuickSchedule={newThreadOnly ? handleQuickScheduleSubmit : undefined}
          />
          {composingField}
          <ActionButton
            // Uploads now run in the background after the message is sent, so
            // the composer is never in a foreground "uploading" state.
            isUploading={false}
            isSendingMessage={isSendingMessage}
            sendInitialMessageMutationPending={sendInitialMessageMutation.isPending}
            threadActivityState={thread?.activityState}
            stopMutationPending={stopMutation.isPending}
            canSend={canSend}
            onStopExecution={handleStopExecution}
          />
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileSelect}
        accept={supportedTypes || UI_CONSTANTS.DEFAULT_ACCEPT_TYPES}
      />
    </form>
  );
};
